import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'

interface FoundingHostConfig {
  feePercentage: number
  stripeFeePercentage: number
  stripeFeeFixed: number
  limit: number
}

export const useFoundingHostCount = () => {
  const [count, setCount] = useState(0)
  const [limit, setLimit] = useState(30)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch count using RPC function
        const { data: countData, error: countError } = await supabase
          .rpc('get_founding_host_count')
        
        if (countError) throw countError
        setCount(countData || 0)

        // Fetch limit from platform_config
        const { data: configData } = await supabase
          .from('platform_config')
          .select('value')
          .eq('key', 'founding_host_limit')
          .single()
        
        if (configData) {
          setLimit(parseInt(configData.value, 10) || 30)
        }
      } catch (error) {
        console.error('Error fetching founding host data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [])

  return { count, limit, isLoading, slotsAvailable: limit - count > 0 }
}

export const useFoundingHostStatus = () => {
  const { user } = useAuth()
  const [isFoundingHost, setIsFoundingHost] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchStatus = async () => {
      if (!user) {
        setIsFoundingHost(false)
        setIsLoading(false)
        return
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('is_founding_host')
          .eq('user_id', user.id)
          .single()

        if (error) throw error
        setIsFoundingHost(data?.is_founding_host || false)
      } catch (error) {
        console.error('Error fetching founding host status:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchStatus()
  }, [user])

  return { isFoundingHost, isLoading }
}

export const useFoundingHostConfig = () => {
  const [config, setConfig] = useState<FoundingHostConfig>({
    feePercentage: 0,
    stripeFeePercentage: 2.9,
    stripeFeeFixed: 0.30,
    limit: 30
  })
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const { data, error } = await supabase
          .from('platform_config')
          .select('key, value')
          .in('key', [
            'founding_host_fee_percentage',
            'founding_host_stripe_fee_percentage',
            'founding_host_stripe_fee_fixed',
            'founding_host_limit'
          ])

        if (error) throw error

        const configMap = data?.reduce((acc, item) => {
          acc[item.key] = item.value
          return acc
        }, {} as Record<string, string>) || {}

        setConfig({
          feePercentage: parseFloat(configMap.founding_host_fee_percentage || '0'),
          stripeFeePercentage: parseFloat(configMap.founding_host_stripe_fee_percentage || '2.9'),
          stripeFeeFixed: parseFloat(configMap.founding_host_stripe_fee_fixed || '0.30'),
          limit: parseInt(configMap.founding_host_limit || '30', 10)
        })
      } catch (error) {
        console.error('Error fetching founding host config:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchConfig()
  }, [])

  return { config, isLoading }
}

export const useCanRegisterAsFoundingHost = () => {
  const [canRegister, setCanRegister] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const checkAvailability = async () => {
      try {
        const { data, error } = await supabase
          .rpc('can_register_as_founding_host')

        if (error) throw error
        setCanRegister(data || false)
      } catch (error) {
        console.error('Error checking founding host availability:', error)
      } finally {
        setIsLoading(false)
      }
    }

    checkAvailability()
  }, [])

  return { canRegister, isLoading }
}

export const useRegisterAsFoundingHost = () => {
  const { user } = useAuth()
  const [isRegistering, setIsRegistering] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const register = useCallback(async () => {
    if (!user) {
      setError('Bitte melden Sie sich zuerst an.')
      return false
    }

    setIsRegistering(true)
    setError(null)

    try {
      // Check if slots are available
      const { data: canRegister, error: checkError } = await supabase
        .rpc('can_register_as_founding_host')

      if (checkError) throw checkError
      
      if (!canRegister) {
        setError('Leider sind alle Gründerhost-Plätze bereits vergeben.')
        return false
      }

      // Update profile to founding host
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          is_founding_host: true,
          founding_host_registered_at: new Date().toISOString(),
          host_approved: true,
          host_application_status: 'approved',
          user_type: 'host'
        })
        .eq('user_id', user.id)

      if (updateError) throw updateError

      return true
    } catch (err: any) {
      console.error('Error registering as founding host:', err)
      setError(err.message || 'Ein Fehler ist aufgetreten.')
      return false
    } finally {
      setIsRegistering(false)
    }
  }, [user])

  return { register, isRegistering, error }
}
